<script setup>
import { computed } from 'vue'
import { menuItems } from '@/core/constants/menu-items.constant'
import { injectRiskOverviewMenuItemIfAllowed } from '@/core/utils/inject-risk-overview-menu.util'
import { useAuthStore } from '@/modules/auth/stores/auth.store'

const authStore = useAuthStore()

const menuItemsWithRiskOverview = computed(() => {
  const local = JSON.parse(JSON.stringify(menuItems))
  injectRiskOverviewMenuItemIfAllowed(local, authStore.companyData?.id)
  return local
})

function isAuthorized(item) {
  if (item.allowedRoles && item.allowedRoles.length > 0) {
    const hasPermission = authStore.userRoles?.some(role =>
      item.allowedRoles.includes(role),
    )
    if (!hasPermission) {
      return null
    }
  }

  if (item.module) {
    const isModuleEnabled = authStore.tenantData?.enabledModules?.includes(item.module)
    if (!isModuleEnabled) {
      return null
    }
  }

  if (item.children && item.children.length > 0) {
    const filteredChildren = item.children
      .map(child => isAuthorized(child))
      .filter(Boolean)

    if (filteredChildren.length === 0) {
      return null
    }

    return {
      ...item,
      children: filteredChildren,
    }
  }

  return item
}

// Compute the filtered menu items
const filteredMenuItems = computed(() => {
  const filtered = {
    core: [],
    modules: [],
  }

  // Process core menu items: apply authorization and filter out unauthorized items.
  const items = menuItemsWithRiskOverview.value

  if (items.core) {
    filtered.core = items.core
      .map(item => isAuthorized(item))
      .filter(Boolean)
  }

  // Process modules menu items similarly.
  if (items.modules) {
    filtered.modules = items.modules
      .map(item => isAuthorized(item))
      .filter(Boolean)

    // If the company's dashboard data is enabled, process each module's children.
    if (authStore.companyData?.enabledDashboards) {
      filtered.modules = filtered.modules.map((module) => {
        return {
          ...module,
          children:
            module.children?.map((child) => {
              // Optional: Adjust a specific child name if needed.
              if (
                child.name
                === 'core.menu.modules.stop-safety-observation'
              ) {
                if (
                  authStore.companyData?.identifier
                  === 'adecoagro'
                ) {
                  child.name
                    = 'core.menu.modules.stop-safety-observation-adecoagro'
                }
              }

              // Retrieve the dashboards based on the child's module identifier.
              const moduleIdentifier = child.module
              const moduleDashboards
                = authStore.companyData.enabledDashboards[
                  moduleIdentifier
                ] || []

              // Start with any existing children.
              let combinedChildren = child.children
                ? [...child.children]
                : []

              // If there are dashboard entries, create link objects and append them.
              if (moduleDashboards.length) {
                const dashboardLinks = moduleDashboards.map(
                  dashboard => ({
                    name:
                      typeof dashboard.name === 'object'
                        ? dashboard.name.en
                        || Object.values(
                          dashboard.name,
                        )[0]
                        : dashboard.name,
                    route: {
                      name: 'Dashboard Viewer',
                      params: {
                        dashboardId: dashboard._id,
                      },
                    },
                    type: 'sub',
                    isRawName: true,
                  }),
                )

                combinedChildren = [
                  ...combinedChildren,
                  ...dashboardLinks,
                ]
              }

              // Reorder: items whose names end with "module-settings" should come last.
              const nonModuleSettings = combinedChildren.filter(
                item =>
                  !(
                    item.name
                    && item.name.endsWith('module-settings')
                  ),
              )
              const moduleSettings = combinedChildren.filter(
                item =>
                  item.name
                  && item.name.endsWith('module-settings'),
              )
              const sortedChildren = [
                ...nonModuleSettings,
                ...moduleSettings,
              ]

              return {
                ...child,
                children: sortedChildren,
              }
            }) || [],
        }
      })
    }
  }

  return filtered
})
</script>

<template>
  <div
    id="app-sidebar"
    class="w-72 h-[calc(100vh-192px)] min-h-[800px] bg-dss-secondary-500 flex flex-col shrink-0 rounded-3xl sticky top-10"
  >
    <div class="mx-6 my-8">
      <dss-company />
    </div>

    <nav class="flex flex-col overflow-y-auto scrollable-menu">
      <dss-menu-item
        v-for="item in filteredMenuItems.core"
        :key="item.name"
        :item="{ ...item, type: 'core' }"
      />

      <dss-menu-divider />

      <dss-menu-item
        v-for="module in filteredMenuItems.modules"
        :key="module.name"
        :item="{ ...module, type: 'module' }"
      />
    </nav>

    <div class="mb-4 mt-auto">
      <dss-menu-settings />
    </div>
  </div>
</template>
